import React, { useCallback, useEffect, useState } from 'react';
import { analyserFetchJSON } from '../shared/analyserApi';
import { useDashboardLocale } from '../shared/dashboardLocale';
import { ThreeLineStack } from '../shared/ThreeLineStack';
import { ibkrH3, ibkrMuted } from '../kiwoom/kiwoomTheme';

export type IbkrTfsaRow = {
  ticker: string;
  strategy: string;
  signal_on: boolean;
  close: number | null;
  best_upgrade_rank?: number | null;
  latest_action?: string | null;
  latest_action_date?: string | null;
  max_stock_success_rate?: number | null;
  upside_pct?: number | null;
  cond_rank_upgrade: boolean;
  cond_success_rate: boolean;
  cond_upside: boolean;
  conviction: boolean;
};

export type IbkrTfsaLiveResponse = {
  fetched_at?: string;
  mode?: string;
  as_of?: string | null;
  month?: string | null;
  calls_used?: number;
  calls_limit?: number;
  signal_on_count?: number;
  conviction_count?: number;
  queried_count?: number;
  rows?: IbkrTfsaRow[];
  issues?: string[];
};

const th: React.CSSProperties = {
  textAlign: 'left',
  color: '#d81222',
  fontSize: '0.7rem',
  padding: '6px 8px',
  borderBottom: '1px solid #d8122244',
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  color: '#f5f0e6',
  fontSize: '0.75rem',
  padding: '6px 8px',
  borderBottom: '1px solid #d8122222',
  verticalAlign: 'top',
};

function Stat({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  return (
    <div className="ibkr-stat-item">
      <div className="ibkr-stat-label">{label}</div>
      <div className="ibkr-stat-value" style={{ color: color ?? '#f5f0e6' }}>{value}</div>
    </div>
  );
}

function formatSuccessRate(rate: number | null | undefined): string {
  if (rate == null) return '–';
  return `${(rate * 100).toFixed(0)}%`;
}

function analystActionColor(action: string | null | undefined): string {
  if (!action) return ibkrMuted;
  const a = action.toLowerCase();
  if (a === 'upgraded' || a === 'initiated') return '#3fb950';
  if (a === 'downgraded') return '#f85149';
  return ibkrMuted;
}

const IbkrTfsaPaperPanel: React.FC = () => {
  const { locale, t } = useDashboardLocale();
  const [data, setData] = useState<IbkrTfsaLiveResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await analyserFetchJSON<IbkrTfsaLiveResponse>(
        'ibkr',
        '/ibkr-tfsa-live',
        { timeoutMs: 20000 },
      );
      setData(next);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.rows ?? [];
  const callsUsed = data?.calls_used ?? 0;
  const callsLimit = data?.calls_limit ?? 50;

  return (
    <div
      className="ibkr-swing-panel ibkr-tfsa-panel"
      style={{
        padding: '14px 16px',
        borderRadius: 8,
        border: '1px solid #d8122266',
        background: 'linear-gradient(135deg, rgba(216,18,34,0.08) 0%, rgba(0,0,0,0.35) 100%)',
      }}
    >
      <div className="binance-alert-header">
        <div className="binance-alert-header-copy">
          <h3 style={{ ...ibkrH3, marginTop: 0, marginBottom: 4 }}>TFSA Paper</h3>
          <div className="ibkr-alert-subtitle" style={{ color: ibkrMuted, fontSize: '0.72rem', lineHeight: 1.45 }}>
            {t('ibkr.tfsa.subtitle.line1')}
            <br />
            {t('ibkr.tfsa.subtitle.line2')}
            <br />
            {t('ibkr.tfsa.subtitle.line3')}
          </div>
        </div>
        <button
          type="button"
          className="ibkr-alert-refresh"
          onClick={() => { setLoading(true); load(); }}
        >
          {t('common.refresh')}
        </button>
      </div>

      {loading && !data && <div style={{ color: ibkrMuted, fontSize: '0.78rem' }}>{t('common.loading')}</div>}
      {error && (
        <div style={{ color: '#f85149', fontSize: '0.78rem', marginBottom: 8 }}>{error}</div>
      )}

      {data && (
        <>
          <div className="ibkr-alert-stats">
            <Stat label="as of" value={data.as_of ?? '–'} />
            <Stat
              label="calls"
              value={`${callsUsed}/${callsLimit}`}
              color={callsUsed >= callsLimit ? '#f85149' : '#f5f0e6'}
            />
            <Stat label="signal on" value={data.signal_on_count ?? 0} color="#f0b90b" />
            <Stat
              label="conviction"
              value={data.conviction_count ?? 0}
              color={(data.conviction_count ?? 0) > 0 ? '#3fb950' : ibkrMuted}
            />
            <Stat label="queried" value={data.queried_count ?? 0} />
            <Stat label="month" value={data.month ?? '–'} />
            <Stat label="mode" value={data.mode ?? 'paper'} />
            <Stat label="rows" value={rows.length} />
            <Stat
              label="issues"
              value={(data.issues ?? []).length}
              color={(data.issues ?? []).length > 0 ? '#f85149' : '#3fb950'}
            />
          </div>

          <div className="ibkr-swing-section-title">{t('ibkr.tfsa.section.analystFilter')}</div>
          <div className="binance-alert-table-wrap">
            <table key={locale} className="binance-alert-table ibkr-swing-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th className="ibkr-tfsa-col-ticker" style={th}>{t('ibkr.tfsa.table.ticker')}</th>
                  <th className="ibkr-tfsa-col-signal" style={th}>{t('ibkr.tfsa.table.signal')}</th>
                  <th className="ibkr-tfsa-col-analyst" style={th}>{t('ibkr.tfsa.table.analyst')}</th>
                  <th className="ibkr-tfsa-col-sr" style={th}>{t('ibkr.tfsa.table.sr')}</th>
                  <th className="ibkr-tfsa-col-upside" style={th}>{t('ibkr.tfsa.table.upside')}</th>
                  <th className="ibkr-tfsa-col-conv" style={th}>{t('ibkr.tfsa.table.conv')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ ...td, color: ibkrMuted, textAlign: 'center' }}>
                      {t('ibkr.tfsa.table.empty')}
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr
                    key={`${row.ticker}-${row.strategy}`}
                    className={row.signal_on && row.conviction ? 'broker-row-open' : undefined}
                  >
                    <td className="ibkr-tfsa-col-ticker" style={{ ...td, wordBreak: 'break-word' }}>
                      <ThreeLineStack
                        mutedColor={ibkrMuted}
                        lines={[row.ticker, row.strategy, null]}
                      />
                    </td>
                    <td className="ibkr-tfsa-col-signal" style={td}>
                      <ThreeLineStack
                        mutedColor={ibkrMuted}
                        lines={[
                          <span style={{ color: row.signal_on ? '#3fb950' : ibkrMuted, fontWeight: 600 }}>
                            {row.signal_on ? 'ON' : 'off'}
                          </span>,
                          row.close != null ? row.close.toFixed(2) : null,
                          null,
                        ]}
                      />
                    </td>
                    <td className="ibkr-tfsa-col-analyst" style={td}>
                      <ThreeLineStack
                        mutedColor={ibkrMuted}
                        lines={[
                          <span style={{ color: row.cond_rank_upgrade ? '#3fb950' : '#f5f0e6', fontWeight: 600 }}>
                            {row.best_upgrade_rank != null ? `#${row.best_upgrade_rank}` : '–'}
                          </span>,
                          <span style={{ color: analystActionColor(row.latest_action), wordBreak: 'break-word' }}>
                            {row.latest_action ?? '–'}
                          </span>,
                          row.latest_action_date || null,
                        ]}
                      />
                    </td>
                    <td className="ibkr-tfsa-col-sr" style={td}>
                      <ThreeLineStack
                        mutedColor={ibkrMuted}
                        lines={[
                          <span style={{ color: row.cond_success_rate ? '#3fb950' : '#f5f0e6' }}>
                            {formatSuccessRate(row.max_stock_success_rate)}
                          </span>,
                          null,
                          null,
                        ]}
                      />
                    </td>
                    <td className="ibkr-tfsa-col-upside" style={td}>
                      <ThreeLineStack
                        mutedColor={ibkrMuted}
                        lines={[
                          <span style={{ color: row.cond_upside ? '#3fb950' : '#f5f0e6' }}>
                            {row.upside_pct != null ? `${row.upside_pct.toFixed(1)}%` : '–'}
                          </span>,
                          null,
                          null,
                        ]}
                      />
                    </td>
                    <td className="ibkr-tfsa-col-conv" style={td}>
                      <ThreeLineStack
                        mutedColor={ibkrMuted}
                        lines={[
                          <span style={{ color: row.conviction ? '#3fb950' : ibkrMuted, fontWeight: 600 }}>
                            {row.conviction ? 'YES' : 'no'}
                          </span>,
                          null,
                          null,
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(data.issues?.length ?? 0) > 0 && (
            <div
              style={{
                marginTop: 10,
                padding: '8px 10px',
                borderRadius: 6,
                background: 'rgba(248,81,73,0.08)',
                border: '1px solid #f8514944',
                fontSize: '0.68rem',
                color: '#ffa198',
                lineHeight: 1.4,
              }}
            >
              {data.issues!.map((x, i) => <div key={i}>{x}</div>)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IbkrTfsaPaperPanel;
